import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useBackNavigation } from '../../hooks/useBackNavigation.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { api } from '../../services/api.js';
import { fetchAllPages } from '../../services/fetchAllPages.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface Car {
  _id: string;
  name?: string;
}
interface FineData {
  car?: { id: string } | null;
  fine_type?: string;
  amount_minor?: number;
  currency_code?: string;
  issued_at?: string;
  due_at?: string;
  reference_no?: string;
  booking_id?: string;
  liable_user?: { id: string } | null;
  status?: string;
  notes?: string;
  attachment_file_asset_id?: string;
}

function toLocalDate(v?: string) {
  return v ? v.slice(0, 10) : '';
}
function toIso(v?: string) {
  return v ? new Date(v).toISOString() : undefined;
}

export function FineFormPage() {
  const navigate = useNavigate();
  const goBack = useBackNavigation('/fines');
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState<Record<string, string>>({
    car_id: '',
    fine_type: 'traffic',
    amount: '',
    currency_code: 'MYR',
    issued_at: '',
    due_at: '',
    reference_no: '',
    booking_id: '',
    liable_user_id: '',
    status: 'unpaid',
    notes: '',
  });
  const [attachmentId, setAttachmentId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const { data: cars } = useQuery({
    queryKey: RESOURCE_KEYS.car.list,
    // 162 (US3): every car, not the first page of 20. `/cars` paginates with a default of 20 and
    // a maximum of 100, and this control read it with no page parameter at all — so on a fleet
    // over 20 cars the car you wanted was simply not in the list, with no error and no note.
    queryFn: () => fetchAllPages<Car>('/cars', (url) => api.get(url)),
  });

  const { data: existing } = useQuery({
    queryKey: RESOURCE_KEYS.fine.one(id as string),
    queryFn: () => api.get<FineData>(`/fines/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    setForm((f) => ({
      ...f,
      car_id: existing.car?.id ?? '',
      fine_type: existing.fine_type ?? 'traffic',
      amount: existing.amount_minor != null ? (existing.amount_minor / 100).toFixed(2) : '',
      currency_code: existing.currency_code ?? 'MYR',
      issued_at: toLocalDate(existing.issued_at),
      due_at: toLocalDate(existing.due_at),
      reference_no: existing.reference_no ?? '',
      booking_id: existing.booking_id ?? '',
      liable_user_id: existing.liable_user?.id ?? '',
      status: existing.status ?? 'unpaid',
      notes: existing.notes ?? '',
    }));
    if (existing.attachment_file_asset_id) setAttachmentId(existing.attachment_file_asset_id);
  }, [existing]);

  function buildPayload(): Record<string, unknown> {
    const out: Record<string, unknown> = {
      fine_type: form.fine_type,
      amount_minor: Math.round(Number(form.amount) * 100),
      currency_code: form.currency_code,
      issued_at: toIso(form.issued_at),
      status: form.status,
    };
    if (!isEdit) out.car_id = form.car_id;
    if (form.due_at) out.due_at = toIso(form.due_at);
    if (form.reference_no) out.reference_no = form.reference_no;
    if (form.booking_id) out.booking_id = form.booking_id;
    if (form.liable_user_id) out.liable_user_id = form.liable_user_id;
    if (form.notes) out.notes = form.notes;
    if (attachmentId) out.attachment_file_asset_id = attachmentId;
    return out;
  }

  async function handleUpload(file: File) {
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'other');
      const res = (await api.upload('/files/upload', fd)) as { id: string };
      setAttachmentId(res.id);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  // 161 (US2): this form invalidated nothing, so the fines list kept the old row after a save.
  const { save, isPending } = useSaveMutation({
    resource: 'fine',
    id,
    mutationFn: () =>
      isEdit ? api.patch(`/fines/${id}`, buildPayload()) : api.post('/fines', buildPayload()),
    onSaved: () => navigate('/fines'),
    onError: (err) => setError((err as { message?: string }).message ?? 'Failed to save fine'),
  });

  function set(k: string, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Fine' : 'New Fine'}
        description="Record a fine against a car; optionally link a booking and liable renter."
        fallback="/fines"
      />
      <form
        className="max-w-2xl space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          void save();
        }}
      >
        <Card>
          <div className="space-y-4">
            {!isEdit && (
              <FormField label="Car" name="car_id" required>
                <Select
                  id="car_id"
                  value={form.car_id}
                  onChange={(e) => set('car_id', (e.target as HTMLSelectElement).value)}
                  required
                >
                  <option value="" disabled>
                    Select a car…
                  </option>
                  {cars?.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name ?? c._id}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Type" name="fine_type" required>
                <Select
                  id="fine_type"
                  value={form.fine_type}
                  onChange={(e) => set('fine_type', (e.target as HTMLSelectElement).value)}
                >
                  <option value="traffic">Traffic</option>
                  <option value="parking">Parking</option>
                  <option value="toll">Toll</option>
                  <option value="speeding">Speeding</option>
                  <option value="other">Other</option>
                </Select>
              </FormField>
              <FormField label="Status" name="status" required>
                <Select
                  id="status"
                  value={form.status}
                  onChange={(e) => set('status', (e.target as HTMLSelectElement).value)}
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                  <option value="disputed">Disputed</option>
                  <option value="waived">Waived</option>
                </Select>
              </FormField>
              <FormField label="Amount (RM)" name="amount" required>
                <Input
                  id="amount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.amount}
                  onInput={(e) => set('amount', (e.target as HTMLInputElement).value)}
                  required
                />
              </FormField>
              <FormField label="Issued at" name="issued_at" required>
                <Input
                  id="issued_at"
                  type="date"
                  value={form.issued_at}
                  onInput={(e) => set('issued_at', (e.target as HTMLInputElement).value)}
                  required
                />
              </FormField>
              <FormField label="Due at" name="due_at">
                <Input
                  id="due_at"
                  type="date"
                  value={form.due_at}
                  onInput={(e) => set('due_at', (e.target as HTMLInputElement).value)}
                />
              </FormField>
              <FormField label="Reference no." name="reference_no">
                <Input
                  id="reference_no"
                  value={form.reference_no}
                  onInput={(e) => set('reference_no', (e.target as HTMLInputElement).value)}
                />
              </FormField>
              <FormField label="Booking ID" name="booking_id" hint="Optional — link to a booking">
                <Input
                  id="booking_id"
                  value={form.booking_id}
                  onInput={(e) => set('booking_id', (e.target as HTMLInputElement).value)}
                />
              </FormField>
              <FormField label="Liable renter (user ID)" name="liable_user_id" hint="Optional">
                <Input
                  id="liable_user_id"
                  value={form.liable_user_id}
                  onInput={(e) => set('liable_user_id', (e.target as HTMLInputElement).value)}
                />
              </FormField>
            </div>
            <FormField
              label="Ticket attachment"
              name="attachment"
              hint="Optional photo of the ticket"
            >
              <input
                id="attachment"
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) void handleUpload(file);
                }}
              />
              {uploading && <span className="ml-2 text-xs text-steel">Uploading…</span>}
              {attachmentId && !uploading && (
                <span className="ml-2 text-xs text-green-600 inline-flex items-center gap-1">
                  Attached
                  <Check size={12} aria-hidden="true" />
                </span>
              )}
            </FormField>
            <FormField label="Notes" name="notes">
              <Textarea
                id="notes"
                rows={2}
                value={form.notes}
                onInput={(e) => set('notes', (e.target as HTMLTextAreaElement).value)}
              />
            </FormField>
          </div>
        </Card>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3">
          <Button type="submit" loading={isPending}>
            {isEdit ? 'Save Changes' : 'Create Fine'}
          </Button>
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
